import React, { useEffect, useState, useContext } from "react";
import { doc, getDoc, getDocs, collection, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { StudentContext } from "../contexts/StudentContext";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";


const Dashboard = () => {
  const { studentData } = useContext(StudentContext);
  const [courseInfo, setCourseInfo] = useState(null);
  const [courseLoading, setCourseLoading] = useState(false);
  const [courseError, setCourseError] = useState(null);
  const [days, setDays] = useState([]);
  const [userSubmissions, setUserSubmissions] = useState({});
  const [currentAccessibleDay, setCurrentAccessibleDay] = useState(1);
  const [nextDayUnlockTime, setNextDayUnlockTime] = useState(null);
  const [courseStartDate, setCourseStartDate] = useState(null);
  const navigate = useNavigate();

  // Cache management
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  const getCachedData = (key) => {
    const cached = localStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL) {
        return data;
      }
      localStorage.removeItem(key);
    }
    return null;
  };

  const setCachedData = (key, data) => {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  };

  // Helper: Normalize courseStartDate (same as DailyModule)
  const normalizeCourseStartDate = (date) => {
    if (!date) {
      console.warn("No courseStartDate, defaulting to today");
      return new Date();
    }
    if (date.toDate) {
      const d = date.toDate();
      return isNaN(d.getTime()) ? new Date() : d;
    }
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  };

  // Helper: Get unlock date for a day (same as DailyModule)
  const getUnlockDate = (dayNumber, startDate) => {
    if (!startDate) {
      console.warn("No startDate for unlockDate, using today");
      return new Date();
    }
    const unlockDate = new Date(startDate);
    unlockDate.setDate(startDate.getDate() + (dayNumber - 1));
    unlockDate.setHours(0, 0, 0, 0);
    return unlockDate;
  };

  // Function to fetch user submissions for all days
  const fetchUserSubmissions = async (courseId) => {
    try {
      if (!studentData?.id) return {};

      // Check cache first
      const cacheKey = `submissions_${studentData.id}_${courseId}`;
      const cached = getCachedData(cacheKey);
      if (cached) {
        return cached;
      }

      const answersRef = collection(db, 'answers');
      const q = query(
        answersRef,
        where('studentId', '==', studentData.id),
        where('courseId', '==', courseId)
      );
      
      const querySnapshot = await getDocs(q);
      const submissions = {};
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        submissions[data.dayId] = {
          ...data,
          docId: doc.id
        };
      });

      // Cache the submissions
      setCachedData(cacheKey, submissions);
      return submissions;
    } catch (error) {
      console.error('Error fetching user submissions:', error);
      return {};
    }
  };

  // Calculate current accessible day (aligned with DailyModule logic)
  const calculateCurrentAccessibleDay = (daysData, submissions, startDate) => {
    let highestSubmittedDay = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const normalizedStartDate = new Date(startDate);
    normalizedStartDate.setHours(0, 0, 0, 0);

    

    if (normalizedStartDate > today) {
      //console.log("Course in future, Day 1");
      return 1;
    }

    if (normalizedStartDate.getTime() === today.getTime() && !Object.keys(submissions).length) {
      //console.log("Course starts today, no submissions, Day 1");
      return 1;
    }

    for (let i = 0; i < daysData.length; i++) {
      const dayData = daysData[i];
      const dayNumber = i + 1;
      const submission = submissions[dayData.id];
      if (submission) {
        const unlockDate = getUnlockDate(dayNumber, startDate);
        
        if (today >= unlockDate) {
          highestSubmittedDay = Math.max(highestSubmittedDay, dayNumber);
        }
      }
    }

    const nextDay = highestSubmittedDay + 1;
    
    
    // Ensure we don't exceed total days
    return Math.min(nextDay, daysData.length);
  };

  // Function to calculate time until next day unlock (simplified for dashboard)
  const calculateNextDayUnlockTime = (daysData, submissions, currentAccessibleDay, startDate) => {
    if (currentAccessibleDay >= daysData.length) return null;

    const currentDayIndex = currentAccessibleDay - 1;
    const currentDayData = daysData[currentDayIndex];
    
    if (!currentDayData) return null;

    // Get the unlock date for the next day
    const nextDayUnlockDate = getUnlockDate(currentAccessibleDay + 1, startDate);
    const now = new Date();
    
    if (now < nextDayUnlockDate) {
      return nextDayUnlockDate;
    }

    return null;
  };

  // Function to format time remaining with countdown
  const formatTimeRemaining = (targetTime) => {
    if (!targetTime) return null;
    
    const now = new Date();
    const timeDiff = targetTime - now;
    
    if (timeDiff <= 0) return null;
    
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        if (!studentData?.uid) {
          throw new Error("Student UID not available");
        }

        setCourseLoading(true);
        setCourseError(null);

        // Check if we can use cached data from DailyModule
        const cacheKey = `course_${studentData.uid}`;
        const cachedCourse = getCachedData(cacheKey);
        
        if (cachedCourse) {
          //console.log("Using cached course data from DailyModule:", cachedCourse);
          setCourseInfo(cachedCourse.courseInfo);
          setDays(cachedCourse.days);
          setUserSubmissions(cachedCourse.submissions);
          setCourseStartDate(new Date(cachedCourse.courseStartDate));
          setCurrentAccessibleDay(cachedCourse.currentAccessibleDay);
          
          // Calculate next day unlock time
          const unlockTime = calculateNextDayUnlockTime(
            cachedCourse.days, 
            cachedCourse.submissions, 
            cachedCourse.currentAccessibleDay,
            new Date(cachedCourse.courseStartDate)
          );
          setNextDayUnlockTime(unlockTime);
          setCourseLoading(false);
          return;
        }

        // Fetch fresh data if no cache available
        const studentRef = doc(db, "students", studentData.uid);
        const studentDoc = await getDoc(studentRef);

        if (!studentDoc.exists()) {
          throw new Error("Student not found");
        }

        const studentDataFromDoc = studentDoc.data();
        const courseId = studentDataFromDoc.course || studentData.course;
        const startDate = normalizeCourseStartDate(studentDataFromDoc.courseStartDate);
        setCourseStartDate(startDate);

        if (!courseId) {
          throw new Error("No course assigned to student");
        }

        // Fetch course data
        const courseRef = doc(db, "courses", courseId);
        const courseDoc = await getDoc(courseRef);

        if (!courseDoc.exists()) {
          throw new Error("Course not found");
        }

        const courseData = { ...courseDoc.data(), id: courseId };
        setCourseInfo(courseData);

        // Fetch days subcollection
        const daysCollectionRef = collection(db, `courses/${courseId}/day`);
        const daysSnapshot = await getDocs(daysCollectionRef);
        
        // Sort days by numeric day order extracted from id
        const daysData = daysSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data()
          }))
          .sort((a, b) => {
            const aNum = parseInt(a.id.replace(/\D/g, '')) || 0;
            const bNum = parseInt(b.id.replace(/\D/g, '')) || 0;
            return aNum - bNum;
          });

        // Fetch user submissions
        const submissions = await fetchUserSubmissions(courseId);
        setUserSubmissions(submissions);

        // Calculate accessible day using DailyModule logic
        const accessibleDay = calculateCurrentAccessibleDay(daysData, submissions, startDate);
        setCurrentAccessibleDay(accessibleDay);

        // Store in localStorage for consistency
        localStorage.setItem(`currentAccessibleDay_${studentData?.id}`, accessibleDay.toString());

        // Calculate next day unlock time
        const unlockTime = calculateNextDayUnlockTime(daysData, submissions, accessibleDay, startDate);
        setNextDayUnlockTime(unlockTime);

        // Transform days data to match DailyModule format
        const transformedDays = daysData.map((doc, i) => {
          const dayNumber = i + 1;
          const submission = submissions[doc.id];
          const isAccessible = dayNumber <= accessibleDay;
          const isCurrentDay = dayNumber === accessibleDay;
          const isCompleted = submission && (submission.status === "Approved" || doc.autoApproval);

          return {
            id: doc.id,
            day: dayNumber,
            dayTitle: doc.dayTitle || "No Title",
            assignmentRequired: doc.assignmentRequired || false,
            autoApproval: doc.autoApproval || false,
            manualApproval: doc.manualApproval || false,
            status: submission?.status || null,
            completed: isCompleted,
            isCurrentDay: isCurrentDay,
            isAccessible: isAccessible,
            isLocked: !isAccessible,
            submission: submission,
          };
        });

        setDays(transformedDays);

      } catch (error) {
        console.error("Error fetching course data:", error);
        setCourseError(error.message);
      } finally {
        setCourseLoading(false);
      }
    };

    fetchCourseData();
  }, [studentData]);

  // Update time remaining every second for real-time countdown
  useEffect(() => {
    const interval = setInterval(() => {
      if (days.length > 0 && userSubmissions && courseStartDate) {
        const unlockTime = calculateNextDayUnlockTime(days, userSubmissions, currentAccessibleDay, courseStartDate);
        setNextDayUnlockTime(unlockTime);
      }
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [days, userSubmissions, currentAccessibleDay, courseStartDate]);

  const handleContinueLearning = () => {
    navigate("/dailymodule", {
      state: {
        courseId: courseInfo?.id,
        dayId: days?.find((day) => day.day === currentAccessibleDay)?.id,
      },
    });
  };

  if (courseLoading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#f9fafb",
        color: "#6b7280",
        fontSize: 18,
      }}>
        <div style={{
          border: "4px solid #e5e7eb",
          borderTop: "4px solid #3b82f6",
          borderRadius: "50%",
          width: 40,
          height: 40,
          animation: "spin 1s linear infinite",
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <p style={{ marginTop: 16 }}>Loading your dashboard...</p>
      </div>
    );
  }

  if (courseError) {
    return (
      <div style={{ color: "#dc2626", textAlign: "center", padding: "2rem" }}>
        <p>Error: {courseError}</p>
        <button 
          onClick={() => window.location.reload()} 
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1rem",
            backgroundColor: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "0.375rem",
            cursor: "pointer"
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Get today's topic title (current accessible day)
  const currentDayData = days.find((day) => day.day === currentAccessibleDay);
  const todayTopic = currentDayData?.dayTitle || "Loading topic...";

  // Calculate completed days based on submissions and approvals
  const completedDaysCount = days.filter((day) => {
    return day.completed && day.day < currentAccessibleDay;
  }).length;

  // Format time remaining for deadline
  const timeRemaining = formatTimeRemaining(nextDayUnlockTime);
  const deadlineText = timeRemaining ? timeRemaining : "Available";
  const deadlineSubtext = timeRemaining ? "Next day unlocks at midnight" : "Ready to continue";

  return (
    <div className="container-fluid py-4" style={{ background: "#f8f9fb", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} />
      <div className="mb-4">
        <h4 className="fw-bold mb-1" style={{ color: "#4f46e5" }}>
          Welcome back, {studentData?.name || "Student"}!
        </h4>
        <p className="text-secondary mb-0">Here's an overview of your progress and upcoming tasks.</p>
      </div>

      <div className="row g-3 mb-4 justify-content-center">
        <div className="col-12 col-md-4 d-flex">
          <div className="card border-0 shadow-sm flex-fill d-flex align-items-stretch" style={{ background: "#17c1e8", color: "#fff", minHeight: 140, maxWidth: 340, width: "100%" }}>
            <div className="card-body d-flex flex-column justify-content-between">
              <div>
                <div className="fw-semibold mb-2">Current Progress</div>
                <div className="fs-5 fw-bold">Day {currentAccessibleDay} of {days.length}</div>
                <div className="small mb-2">{days.length > 0 ? ((currentAccessibleDay / days.length) * 100).toFixed(1) : 0}% completed</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4 d-flex">
          <div className="card border-0 shadow-sm flex-fill d-flex align-items-stretch" style={{ background: "#ffb347", color: "#fff", minHeight: 140, maxWidth: 340, width: "100%" }}>
            <div className="card-body d-flex flex-column justify-content-between">
              <div>
                <div className="fw-semibold mb-2">Next Day Unlock</div>
                <div className="fs-5 fw-bold" style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>{deadlineText}</div>
                <div className="small mb-2">{deadlineSubtext}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4 d-flex">
          <div className="card border-0 shadow-sm flex-fill d-flex align-items-stretch" style={{ background: "#10b981", color: "#fff", minHeight: 140, maxWidth: 340, width: "100%" }}>
            <div className="card-body d-flex flex-column justify-content-between">
              <div>
                <div className="fw-semibold mb-2">Assignments</div>
                <div className="fs-5 fw-bold">{completedDaysCount}/{days.length}</div>
                <div className="small mb-2">{days.length > 0 ? ((completedDaysCount / days.length) * 100).toFixed(1) : 0}% completed</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Course and Learning Path */}
      <div className="row g-3">
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm mb-3 position-relative overflow-hidden" style={{ background: "linear-gradient(90deg, #7f53ac 0%, #647dee 100%)", color: "#fff", minHeight: 300 }}>
            <div className="card-body p-4" style={{ minHeight: '250px', maxWidth: '400px' }}>
              <div className="fw-semibold mb-2 fs-5">Current Course</div>
              
              <div className="mb-3 fs-6 text-truncate">
                {courseInfo?.title || "Loading course..."}
              </div>
              
              <div className="d-flex align-items-center gap-3 mb-4">
                <div
                  className="rounded bg-white bg-opacity-25 d-flex align-items-center justify-content-center shadow"
                  style={{ width: 56, height: 60 }}
                >
                  <i className="bi bi-layout-text-window-reverse fs-2"></i>
                </div>
                <div>
                  <div className="fw-semibold fs-6">
                    Today's Topic:{" "}
                    <span style={{ color: "#ffd600" }}>
                      {todayTopic}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="d-flex gap-2 mt-auto">
                <button
                  className="btn btn-light btn-sm fw-semibold px-3"
                  style={{ color: "#4f46e5" }}
                  onClick={handleContinueLearning}
                >
                  Continue Learning
                </button>
              </div>
            </div>

            <div style={{ position: "absolute", right: -40, bottom: -40, opacity: 0.15, fontSize: 180, pointerEvents: "none" }}>
              <i className="bi bi-mortarboard"></i>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card border-0 shadow-sm" style={{ height: 300 }}>
            <div className="card-body d-flex flex-column">
              <div className="fw-semibold mb-2 fs-5" style={{ color: "#4f46e5" }}>
                Your Learning Path
              </div>
              <div className="text-secondary mb-3" style={{ fontSize: "0.95rem" }}>
                Your next steps to complete the course
              </div>
              <div className="flex-grow-1" style={{ overflowY: 'auto', maxHeight: '200px' }}>
                <ul className="list-unstyled mb-0">
                  {days.map((day, index) => {
                    const isCurrentDay = day.isCurrentDay;
                    const isCompleted = day.completed;
                    const isLocked = day.isLocked;
                    
                    return (
                      <li key={index} className="mb-2 d-flex align-items-center gap-2">
                        <i className={`bi ${
                          isCompleted 
                            ? "bi-check-circle-fill text-success" 
                            : isCurrentDay 
                              ? "bi-play-circle-fill text-primary" 
                              : "bi-lock-fill text-muted"
                        }`}></i>
                        <span className={isCurrentDay ? "fw-semibold text-primary" : ""}>{day.dayTitle}</span>
                        <span className="ms-auto text-secondary small">
                          {isCompleted ? "Completed" : isCurrentDay ? "Current Day" : isLocked ? "Locked" : "Available"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
